import { Condition } from '@api';
import { useFilteredTimeSeries } from '@terminal/react-query-hooks/snakes/useTimeSeries';
import { useEffect, useMemo, useState } from 'react';

import { ConditionSearch } from '../condition-search/ConditionSearch';
import styles from './ConditionDataList.module.scss';

export interface ConditionDataListProps {
  condition: Condition;
  response: (editedPayload: Partial<Condition>) => void;
}

interface Item {
  value: string;
  label: string;
}

export const ConditionDataList = ({
  condition,
  response,
}: ConditionDataListProps) => {
  const { data: snakeItems } = useFilteredTimeSeries([condition.entity]);
  const [items, setItems] = useState<Item[]>([]);
  const [search, setSearch] = useState('');

  const data = useMemo(
    () =>
      snakeItems
        ?.filter(({ search: { rank } }) => rank !== -1)
        .sort((a, b) => b.search.rank - a.search.rank)
        ?.map(i => ({
          label: i.name.english,
          value: i.snake,
        })) ?? [],
    [snakeItems]
  );

  useEffect(() => {
    const query = search && search.length > 1 ? search : condition.snakeName;

    if (query) {
      setItems(
        data.filter(d => d.label.toLowerCase().includes(query.toLowerCase()))
      );
    } else {
      setItems([]);
    }
  }, [data, search, condition.snakeName]);

  const handleSelect = (selectedItem: Item) => {
    const editedPayload = {
      snake: selectedItem.value,
      snakeName: selectedItem.label,
    };
    response(editedPayload);
  };

  return (
    <ConditionSearch
      label={condition.snakeName}
      dataTestId="scenario-data-list"
      onChange={e => setSearch(e.target.value)}
    >
      {items.map(item => (
        <div
          className={styles.item}
          key={item.value}
          data-testid={`condition-data-${item.value}`}
          onClick={() => handleSelect(item)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSelect(item);
            }
          }}
          role="option"
          aria-selected={item.value === condition.snake}
          tabIndex={0}
        >
          {item.label}
        </div>
      ))}
    </ConditionSearch>
  );
};
